import React from 'react'
import { useQuery } from '@tanstack/react-query';
import { fetchPaylink } from '../js/paylink-utility';
import { fetchCustomer, fetchLogin } from '../../common/js/bfx-customer-utility';
import { getStripeKey, resolveStripeGateway } from '../../common/js/stripe-utility';
import { getPayPalGatewayByCurrency, getPayPalKey, hasTestPrivileges } from '../../common/js/paypal-utility';
import Summary from '../../checkout2/components/Summary';
import StripeProvider from '../../common/components/payment-method/StripeProvider';
import { PayPalProvider } from '@paypal/react-paypal-js/sdk-v6';
import { type WebCart } from '../../common/types/webshop.types';
import SpinnerOverlay from '../../common/components/SpinnerOverlay';
import PaylinkBilling from './PaylinkBilling';
import PaylinkError from './PaylinkError';
import Cookies from 'js-cookie';

import { useTranslation } from 'react-i18next'

type Props = {
    paylinkId: string;
}

export default function Paylink({ paylinkId }: Props) {
     const handlePaylinkFetch = async () => {
        const loginData = fetchLogin();
        if (!loginData){
            Cookies.set('redirect_url', window.location.href);
            window.location.href = '/account/login';
            return;
        }
        return await fetchPaylink(paylinkId);
    }

    const {t} = useTranslation('common');

    const orderInfo = useQuery({
        queryKey: ['paylink', 'order'], 
        queryFn: handlePaylinkFetch, 
        refetchOnWindowFocus: false
    });
    const customerInfo = useQuery({
        queryKey: ['paylink', 'customer'], 
        queryFn: fetchCustomer, 
        refetchOnWindowFocus: false
    });

    const order: WebCart = orderInfo.data ? orderInfo.data.data : null;
    const customer = customerInfo.data;

    const gatewayKey = React.useMemo(() => {
        return getStripeKey(resolveStripeGateway(customer));
    }, [customer])

    const isTest = hasTestPrivileges(customer);
    const paypalGateway = getPayPalGatewayByCurrency(order?.currencyIso ?? '', isTest);
    const paypalClientId = getPayPalKey(paypalGateway);
    const paypalEnv: 'sandbox' | 'production' = isTest ? 'sandbox' : 'production';

    if (orderInfo.data && orderInfo.data.error != null){
        return (
            <div>
                <PaylinkError error={orderInfo.data} />
            </div>
        )
    } 

    if (!order || !customer){
        return <SpinnerOverlay fullscreen opaque size={125} message='Please Wait...' />
    }

    return (
        <div className='paylink-container'>
            <div className='paylink-content'>
                <div style={{minWidth: '450px'}}>
                    <h2 className='desktop-heading' style={{fontSize: '28px', lineHeight: '28px'}}>{t('purchase.invoice')} #{order.invoiceNum}</h2>
                    <StripeProvider publicKey={gatewayKey}>
                        <PayPalProvider key={paypalGateway} clientId={paypalClientId} environment={paypalEnv}>
                            <PaylinkBilling customer={customer} order={order} />
                        </PayPalProvider>
                    </StripeProvider>
                </div>
                <div style={{marginTop: '52px'}}>
                    <h2 className='mobile-heading' style={{fontSize: '28px', lineHeight: '28px'}}>{t('purchase.invoice')} #{order.invoiceNum}</h2>
                    <Summary order={order} customer={customer} isPaylink />
                </div>
            </div>
        </div>
    )
}

